import React, { useState, useEffect, useRef } from "react";
import "./StatisticGraphicsModal.css";
import { useTranslation } from "react-i18next";
import modal_logo from "../../../../_assets/img/sema-logo.png";
import modal_logo_min from "../../../../_assets/img/modal_logo_min.png";
import { Modal } from "react-bootstrap";
import { Button } from "primereact/button";
import { Chart as PrimeChart } from "primereact/chart";
import { getLayerAttributes } from "../../../../_config/layers/zcm";
import 'chart.js/auto';

const StatisticGraphicsModal = ({ active, callback, map }) => {
  const { t } = useTranslation();
  const chartRef = useRef(null);

  const clientWidth = document.documentElement.clientWidth;
  const layers = [...map.getLayers().getArray()];

  const [layerOptions, setLayerOptions] = useState([]);
  const [show, setShow] = useState(false);

  const [chartData, setChartData] = useState(null);
  const [chartOptions, setChartOptions] = useState(null);
  const [chartType, setChartType] = useState("bar");

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  useEffect(() => {
    if (active) handleShow();
  }, [active]);

  useEffect(() => {
    callback(show);
  }, [show]);

  useEffect(() => {
    buildLayerSelect();
  }, []);

  /* =========================================================
     LAYERS
  ========================================================= */
  const buildLayerOption = (layer, id) => (
    <option key={id} value={layer.get("wmsName").split(":")[1]}>
      {layer.get("title")}
    </option>
  );

  function buildLayerSelect() {
    setLayerOptions(
      layers
        .filter((l) => l.get("isAllowedToStatistics"))
        .map((l, i) => buildLayerOption(l, i))
    );
  }

  /* =========================================================
     ATRIBUTOS
  ========================================================= */
  async function loadAttributes() {
    try {
      const layerName = document.getElementById("layerName").value;
      showLoader(true);

      const { wfsAttributes } = await getLayerAttributes(layerName);

      const attributes = wfsAttributes.match(/[\w]+:/g).map((a) => a.slice(0, -1));
      const types = wfsAttributes.match(/[\w]+,/g).map((t) => t.slice(0, -1));

      populateAttributes(attributes, types);
      showLoader(false);
    } catch (e) {
      console.error(e);
      showLoader(false);
    }
  }

  function populateAttributes(attrs, types) {
    const fieldX = document.getElementById("fieldX");
    const fieldY = document.getElementById("fieldY");

    fieldX.innerHTML = "";
    fieldY.innerHTML = "";

    attrs.forEach((attr, i) => {
      if (isTextualOrDate(types[i]) || attr.includes("ano")) {
        fieldX.appendChild(new Option(attr, attr));
      }
      if (isNumericOrDate(types[i])) {
        fieldY.appendChild(new Option(attr, attr));
      }
    });
  }

  const isTextualOrDate = (t) =>
    ["character", "varchar", "text", "date", "timestamp", "time", "string"].some(
      (x) => t.startsWith(x)
    );

  const isNumericOrDate = (t) =>
    ["integer", "int", "float", "double", "bigdecimal", "long", "date"].some(
      (x) => t.startsWith(x)
    );

  /* =========================================================
     CHART
  ========================================================= */
  function generateChart(e) {
    e.preventDefault();

    const layerName = document.getElementById("layerName").value;
    const type = document.getElementById("chartType").value;
    const fieldX = document.getElementById("fieldX").value;
    const fieldY = document.getElementById("fieldY").value;

    setChartType(type);

    const url = `https://pedea.sema.ce.gov.br/geoserver/wfs?service=WFS&version=1.0.0&request=GetFeature&typeName=${layerName}&outputFormat=application/json`;

    showLoader(true);

    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        const x = [];
        const y = [];

        data.features.forEach((f) => {
          x.push(f.properties[fieldX]);
          y.push(f.properties[fieldY]);
        });

        const agg = aggregateData(x, y);
        renderChart(type, agg.xValues, agg.yValues, fieldX, fieldY);
        showLoader(false);
      })
      .catch(() => showLoader(false));
  }

  function aggregateData(x, y) {
    const acc = {};
    x.forEach((v, i) => (acc[v] = (acc[v] || 0) + y[i]));
    return { xValues: Object.keys(acc), yValues: Object.values(acc) };
  }

  function renderChart(type, labels, values, fx, fy) {
    const semaColors = ["#26a737", "#5db12f", "#2db39e", "#fccf05", "#ef4e00"];

    setChartData({
      labels,
      datasets: [
        {
          label: `${fy} por ${fx}`,
          data: values,
          backgroundColor: semaColors,
          borderColor: semaColors,
          borderWidth: 1,
          tension: type === "line" ? 0.4 : 0
        }
      ]
    });

    setChartOptions({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
          labels: {
            font: {
              family: "Kanit",
              size: 14,
              weight: "600"
            }
          }
        },
        tooltip: {
          padding: 12
        }
      },
      scales:
        type === "pie"
          ? {}
          : {
            x: {
              ticks: {
                maxRotation: 45,
                minRotation: 45
              }
            },
            y: {
              beginAtZero: true
            }
          }
    });

    document.getElementById("exportBtn").style.display = "inline-flex";
  }

  /* =========================================================
     EXPORT
  ========================================================= */
  function exportChart() {
    if (!chartRef.current) return;

    // 🔥 PrimeReact expõe o Chart.js assim
    const chart = chartRef.current.chart || chartRef.current.getChart?.();
    if (!chart) return;

    const imageBase64 = chart.toBase64Image("image/png", 1);

    const temp = document.createElement("canvas");
    temp.width = chart.width + 40;
    temp.height = chart.height + 80;

    const ctx = temp.getContext("2d");

    // fundo branco
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, temp.width, temp.height);

    // texto fonte
    ctx.fillStyle = "#000";
    ctx.font = "12px Arial";
    ctx.fillText(
      `Fonte: PEDEA SEMA/CE | Camada: ${document.getElementById("layerName").selectedOptions[0].text
      }`,
      10,
      20
    );

    const img = new Image();
    img.src = imageBase64;
    img.onload = () => {
      ctx.drawImage(img, 20, 40);

      const link = document.createElement("a");
      link.href = temp.toDataURL("image/png");
      link.download = "grafico.png";
      link.click();
    };
  }

  function showLoader(show) {
    document.getElementById("loader").style.display = show ? "block" : "none";
  }

  return (
    <Modal size="xl" show={show} backdrop="static" onHide={handleClose} className="StatisticGraphicsModal">
      {/*<div className="ContainerLogo">
        <img
          src={clientWidth > 350 ? modal_logo : modal_logo_min}
          className="img-thumbnail"
          alt="Logo"
        />
      </div> */}

      <Modal.Body style={{ height: "80vh" }} className="containerStatistic mt-5">
        <h1 className="text-center">{t("statisticModal.title")}</h1>


        <form className="mt-4">

          <div className="Container-inputs">


            <div className="mb-3">

              <label htmlFor="layerName" className="form-label">
                {t("statisticModal.layerLabel")}
              </label>
              <select id="layerName" className="form-control" onChange={loadAttributes}>
                <option value="">{t("statisticModal.layerPlaceholder")}</option>
                {layerOptions}
              </select>

            </div>


            <div id="graphicType" className="mb-3">
              <label htmlFor="chartType" className="form-label">
                {t("statisticModal.chartTypeLabel")}
              </label>
              <select
                className="form-control"
                id="chartType"
                required
                style={{ borderRadius: "10px" }}
              >
                <option value="line">
                  {t("statisticModal.chartType.line")}
                </option>
                <option value="bar">{t("statisticModal.chartType.bar")}</option>
                <option value="pie">{t("statisticModal.chartType.pie")}</option>
              </select>
            </div>



            <div className="mb-3">
              <label htmlFor="fieldX" className="form-label">
                {t("statisticModal.xFieldLabel")}
              </label>
              <select
                className="form-control"
                id="fieldX"
                required
                style={{ borderRadius: "10px" }}
              ></select>
            </div>

            <div className="mb-3">
              <label htmlFor="fieldY" className="form-label">
                {t("statisticModal.yFieldLabel")}
              </label>
              <select
                className="form-control"
                id="fieldY"
                required
                style={{ borderRadius: "10px" }}
              ></select>
            </div>


          </div>

          <Button className="mt-3" onClick={generateChart} icon="pi pi-chart-bar" style={{ borderRadius: "10px", display: "flex", gap: "10px" }} severity="info">
            {t("statisticModal.generateButton")}
          </Button>
        </form>

        {/* LOADER */}
        <div id="loader" className="text-center mt-3">
          <div className="spinner-border" />
        </div>

        {/* CHART */}
        <div  className="ContainerCanvas">
          {chartData && (
            <PrimeChart
              ref={chartRef}
              type={chartType}
              data={chartData}
              options={chartOptions}
              style={{ width: "100%", height: "100%" }}
            />
          )}
        </div>

        <Button
          id="exportBtn"
          className="mt-3"
          severity="success"
          icon="pi pi-file-export"
          onClick={exportChart}
          disabled={!chartData}
          style={{ borderRadius: "10px", display: "flex", gap: "10px" }}
        >
          {t("statisticModal.exportButton")}
        </Button>
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={handleClose}
          severity="secondary"
          icon="pi pi-times"
          raised
          className="BtnCloseGraphics">
          {t("common.close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StatisticGraphicsModal;
